"use client";

import { useEffect, useRef, type JSX } from "react";
import { X } from "lucide-react";
import { useLenisScrollLock } from "@/components/providers/SmoothScrollProvider";

/**
 * Dark-themed inline Calendly dialog.
 *
 * Ported from the mutetaxes-hk `ui/Modal` + Calendly iframe pairing, restyled
 * for this site's dark palette (the `#0f1218` panel / `white/10` hairline used
 * by StickyBottomCta) instead of HK's white card.
 *
 * The surrounding chrome (overlay, panel, close button) is dark to match the
 * site; the Calendly booking widget itself stays on its default white so the
 * calendar reads clearly.
 */

const CALENDLY_THEME_PARAMS: Record<string, string> = {
  // Calendly expects bare hex (no leading `#`).
  background_color: "ffffff",
  text_color: "1a1d24",
  primary_color: "335cff",
  // Trims the widget's own chrome so it needs less vertical room, which is
  // what causes the iframe's internal scrollbar on short viewports.
  hide_gdpr_banner: "1",
  hide_landing_page_details: "1",
};

/** Adds the embed theme params to a Calendly URL, preserving any it already has. */
export const withCalendlyTheme = (url: string): string => {
  try {
    const parsed = new URL(url);
    for (const [key, value] of Object.entries(CALENDLY_THEME_PARAMS)) {
      if (!parsed.searchParams.has(key)) parsed.searchParams.set(key, value);
    }
    // `embed_domain` tells Calendly it's embedded (it drops some standalone
    // page chrome). `embed_type=Inline` is deliberately NOT set: it forces the
    // single narrow column — details stacked above the calendar — which is
    // much taller than the default side-by-side layout and makes the iframe
    // scroll on wide screens.
    if (!parsed.searchParams.has("embed_domain")) {
      parsed.searchParams.set(
        "embed_domain",
        typeof window === "undefined" ? "localhost" : window.location.hostname,
      );
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

interface CalendlyModalProps {
  url: string | null;
  onClose: () => void;
}

export const CalendlyModal = ({ url, onClose }: CalendlyModalProps): JSX.Element | null => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Close on Escape, and lock body scroll while open so the page behind the
  // overlay doesn't scroll along with the iframe.
  useEffect(() => {
    if (!url) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // The document scrolls on <html>, not <body>, so locking `body` alone
    // leaves the page scrollbar visible and active behind the overlay.
    // Removing it also reflows the page, so pad by its exact width to keep
    // the content from jumping sideways as the modal opens.
    const root = document.documentElement;
    const scrollbarWidth = window.innerWidth - root.clientWidth;
    const previous = {
      overflow: root.style.overflow,
      paddingRight: root.style.paddingRight,
    };

    root.style.overflow = "hidden";
    if (scrollbarWidth > 0) root.style.paddingRight = `${scrollbarWidth}px`;

    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previous.overflow;
      root.style.paddingRight = previous.paddingRight;
    };
  }, [url, onClose]);

  // Halt site-wide inertial scrolling while the overlay is up. The lock above
  // already stops the page moving, but Lenis would keep accumulating wheel
  // deltas against a frozen document and then snap to them on close; stopped,
  // it swallows wheel/touch outright. Deliberately declared *after* that
  // effect so the scrollbar is still present when its width is measured.
  useLenisScrollLock(Boolean(url));

  if (!url) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Book a consultation"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-2 backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      {/* The panel never scrolls — `overflow-hidden`. The iframe owns all
          scrolling instead, because a cross-origin iframe swallows the
          wheel/touch gesture: any overflow parked on the panel is unreachable
          from over the widget, which is what left Calendly's bottom content
          (Next / Confirm) permanently cut off in the stacked layout.

          So the panel is sized to the viewport and the iframe fills it exactly;
          when Calendly's content is taller than that, Calendly's own scrollbar
          handles it. */}
      <div
        className="relative flex h-[96vh] max-h-[1040px] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-white/10 bg-white shadow-[0px_8px_40px_rgba(0,0,0,0.6)] lg:h-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="Close booking dialog"
          className="absolute right-2 top-2 z-10 h-9 w-9 rounded-full border border-white/10 bg-[#1a1d24] p-1.5 text-white/80 shadow-md transition hover:bg-[#0f1218] hover:text-white sm:right-3 sm:top-3"
        >
          <X className="h-5 w-5" />
        </button>

        {/* `scrolling` is deliberately left at its default (`auto`) rather than
            `"no"`: below lg Calendly renders the stacked layout, which is taller
            than the panel, and its own scrollbar is the only one the wheel/touch
            gesture can actually reach through the cross-origin boundary.

            `flex-1 min-h-0` makes the iframe consume exactly the panel's height
            so there's no dead band, and at lg+ the fixed 690px matches the
            side-by-side layout with the panel sizing to it. */}
        <iframe
          src={withCalendlyTheme(url)}
          title="Book a consultation"
          allowFullScreen
          className="min-h-0 w-full flex-1 border-0 bg-white lg:h-[690px] lg:flex-none"
        />
      </div>
    </div>
  );
};
